import { create } from 'zustand';
import { GRID_COLS, GRID_ROWS, MAX_MANA, MANA_REGEN_RATE, TOTAL_WAVES, GEM_COSTS, DIFFICULTIES, CURSED_SHARD_COLOR } from './constants.js';
import { generateSeed, getSeedFromURL, updateSeedInURL, createSeededRNG, shuffle, randomInt, areAdjacent, canMerge } from './utils.js';
import { getWaveConfig } from './enemyWaves.js';

// localStorage keys
const STORAGE_KEY_DIFFICULTY = 'gemgrid_difficulty';
const STORAGE_KEY_EXPERT_CLEARED = 'gemgrid_expert_cleared';

// Get difficulty from localStorage with validation
function getStoredDifficulty() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_DIFFICULTY);
    if (stored && DIFFICULTIES[stored]) {
      return stored;
    }
  } catch (e) {
    // localStorage unavailable or corrupted
  }
  return 'polished'; // default
}

// Get expert cleared status
function getExpertCleared() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_EXPERT_CLEARED);
    return stored === 'true';
  } catch (e) {
    return false;
  }
}

// Check if Cursed difficulty is unlocked
export function isCursedUnlocked() {
  return getExpertCleared();
}

// Get current difficulty config
export function getDifficultyConfig(difficultyId = null) {
  const id = difficultyId || getStoredDifficulty();
  return DIFFICULTIES[id] || DIFFICULTIES.polished;
}

// Export DIFFICULTIES for UI use
export { DIFFICULTIES };

export const useGameStore = create((set, get) => ({
  // Game state
  seed: null,
  rng: null,
  phase: 'title', // title, playing, gameover, victory
  
  // Difficulty
  difficulty: 'polished',
  
  // Grid and gems
  grid: [], // 2D array of gems or null
  
  // Mana economy
  mana: 0,
  maxMana: MAX_MANA,
  manaRegenRate: MANA_REGEN_RATE,
  lastManaRegen: 0,
  
  // Battle state
  currentWave: 0,
  waveInProgress: false,
  enemies: [],
  playerUnits: [],
  
  // Cursed Shards (for Cursed difficulty)
  cursedShards: [], // Array of {row, col} positions
  
  // Stats
  gemsMerged: 0,
  highestTierReached: 0,
  startTime: 0,
  elapsedTime: 0,
  
  // Speed
  speedMultiplier: 1,
  
  // Drag state
  draggedGem: null,
  dragStartCell: null,
  isDragging: false,
  
  // Set difficulty (only valid before game starts)
  setDifficulty: (difficultyId) => {
    const state = get();
    // Can only change difficulty during title phase
    if (state.phase !== 'title') return false;
    
    // Validate difficulty
    if (!DIFFICULTIES[difficultyId]) return false;
    
    // Check if Cursed is being selected but not unlocked
    if (difficultyId === 'cursed' && !getExpertCleared()) return false;
    
    // Save to localStorage
    try {
      localStorage.setItem(STORAGE_KEY_DIFFICULTY, difficultyId);
    } catch (e) {
      // localStorage unavailable
    }
    
    set({ difficulty: difficultyId });
    return true;
  },
  
  // Get difficulty config
  getDifficultyConfig: () => {
    const state = get();
    return DIFFICULTIES[state.difficulty] || DIFFICULTIES.polished;
  },
  
  // Initialize game with seed
  initGame: (seed, forcedDifficulty = null) => {
    const state = get();
    
    // Determine difficulty - use forced (for attract mode) or stored
    let difficulty = forcedDifficulty;
    if (!difficulty) {
      // During title phase, read from localStorage
      if (state.phase === 'title') {
        difficulty = getStoredDifficulty();
      } else {
        // Mid-game, keep current difficulty
        difficulty = state.difficulty;
      }
    }
    
    // Validate difficulty
    if (!DIFFICULTIES[difficulty]) {
      difficulty = 'polished';
    }
    
    // Cursed can only be played if unlocked
    if (difficulty === 'cursed' && !getExpertCleared()) {
      difficulty = 'polished';
    }
    
    const diffConfig = DIFFICULTIES[difficulty];
    const finalSeed = seed || getSeedFromURL();
    const rng = createSeededRNG(finalSeed);
    
    // Initialize empty grid
    const grid = Array(GRID_ROWS).fill(null).map(() => Array(GRID_COLS).fill(null));
    
    // Spawn initial gems - 4 random tier-1 gems
    const gemTypes = ['ruby', 'sapphire', 'emerald'];
    const positions = shuffle(
      Array.from({ length: GRID_ROWS * GRID_COLS }, (_, i) => ({
        row: Math.floor(i / GRID_COLS),
        col: i % GRID_COLS
      })),
      rng
    );
    
    for (let i = 0; i < 4; i++) {
      const pos = positions[i];
      const type = gemTypes[randomInt(0, gemTypes.length, rng)];
      grid[pos.row][pos.col] = {
        id: `gem-${pos.row}-${pos.col}-${Date.now()}-${i}`,
        type,
        tier: 1,
        row: pos.row,
        col: pos.col
      };
    }
    
    updateSeedInURL(finalSeed);
    
    set({
      seed: finalSeed,
      rng,
      difficulty,
      phase: 'playing',
      grid,
      mana: diffConfig.startingMana,
      maxMana: MAX_MANA,
      manaRegenRate: diffConfig.manaRegenRate,
      manaFraction: 0,
      lastManaRegen: Date.now(),
      currentWave: 0,
      waveInProgress: false,
      enemies: [],
      playerUnits: [],
      cursedShards: [],
      gemsMerged: 0,
      highestTierReached: 0,
      startTime: Date.now(),
      elapsedTime: 0,
      speedMultiplier: 1,
      draggedGem: null,
      dragStartCell: null,
      isDragging: false
    });
  },
  
  // Update mana regeneration (fractional accumulator so mana increments by 1)
  updateMana: () => {
    const state = get();
    if (state.phase !== 'playing') return;

    const now = Date.now();
    const elapsed = (now - state.lastManaRegen) / 1000;

    const regenRate = state.manaRegenRate || MANA_REGEN_RATE;
    const rawGain = elapsed * regenRate * state.speedMultiplier;
    const totalFraction = (state.manaFraction || 0) + rawGain;
    const wholeGain = Math.floor(totalFraction);
    const remainingFraction = totalFraction - wholeGain;

    const newMana = Math.min(state.maxMana, state.mana + wholeGain);
    set({
      mana: newMana,
      manaFraction: remainingFraction,
      lastManaRegen: now
    });
  },
  
  // Spend mana
  spendMana: (amount) => {
    const state = get();
    if (state.mana >= amount) {
      set({ mana: state.mana - amount });
      return true;
    }
    return false;
  },
  
  // Purchase gem
  purchaseGem: (tier) => {
    const state = get();
    const cost = GEM_COSTS[tier];
    
    if (state.mana < cost) return false;
    
    // Find empty cell (not occupied by gem or cursed shard)
    const emptyCells = [];
    for (let row = 0; row < GRID_ROWS; row++) {
      for (let col = 0; col < GRID_COLS; col++) {
        // Check if cell is empty (no gem) AND not a cursed shard
        const isCursedShard = state.cursedShards.some(s => s.row === row && s.col === col);
        if (!state.grid[row][col] && !isCursedShard) {
          emptyCells.push({ row, col });
        }
      }
    }
    
    if (emptyCells.length === 0) return false;
    
    // Random empty cell
    const pos = emptyCells[randomInt(0, emptyCells.length, state.rng)];
    const gemTypes = ['ruby', 'sapphire', 'emerald'];
    const type = gemTypes[randomInt(0, gemTypes.length, state.rng)];
    
    const newGrid = state.grid.map(row => [...row]);
    newGrid[pos.row][pos.col] = {
      id: `gem-${pos.row}-${pos.col}-${Date.now()}`,
      type,
      tier,
      row: pos.row,
      col: pos.col
    };
    
    get().spendMana(cost);
    set({ grid: newGrid });
    return true;
  },
  
  // Start dragging a gem
  startDrag: (row, col) => {
    const state = get();
    const gem = state.grid[row][col];
    if (!gem) return;
    
    set({
      draggedGem: gem,
      dragStartCell: { row, col },
      isDragging: true
    });
  },
  
  // Move dragged gem
  moveDrag: (row, col) => {
    const state = get();
    if (!state.isDragging) return;
    
    // Just track position for visual update
    // Actual merge happens on drop
  },
  
  // Drop gem (attempt merge)
  dropGem: (targetRow, targetCol) => {
    const state = get();
    if (!state.isDragging || !state.dragStartCell) return;
    
    const { row: startRow, col: startCol } = state.dragStartCell;
    const sourceGem = state.grid[startRow][startCol];
    const targetGem = state.grid[targetRow][targetCol];
    
    // Check if target cell has a cursed shard (can't merge onto cursed shard)
    const isTargetCursedShard = state.cursedShards.some(s => s.row === targetRow && s.col === targetCol);
    if (isTargetCursedShard) {
      // Snap back - can't merge onto cursed shard
      set({
        draggedGem: null,
        dragStartCell: null,
        isDragging: false
      });
      return;
    }
    
    let newGrid = state.grid.map(row => [...row]);
    
    // Check if valid merge
    if (sourceGem && targetGem && 
        areAdjacent({ row: startRow, col: startCol }, { row: targetRow, col: targetCol }) &&
        canMerge(sourceGem, targetGem)) {
      
      // Merge! Create higher tier gem
      const newTier = sourceGem.tier + 1;
      newGrid[targetRow][targetCol] = {
        id: `gem-${targetRow}-${targetCol}-${Date.now()}`,
        type: sourceGem.type,
        tier: newTier,
        row: targetRow,
        col: targetCol
      };
      newGrid[startRow][startCol] = null;

      set({
        grid: newGrid,
        gemsMerged: state.gemsMerged + 1,
        highestTierReached: Math.max(state.highestTierReached, newTier),
        draggedGem: null,
        dragStartCell: null,
        isDragging: false
      });
    } else {
      // Invalid merge - snap back (no state change, just reset drag)
      set({
        draggedGem: null,
        dragStartCell: null,
        isDragging: false
      });
    }
  },
  
  // Cancel drag (snap back)
  cancelDrag: () => {
    set({
      draggedGem: null,
      dragStartCell: null,
      isDragging: false
    });
  },
  
  // Start wave
  startWave: () => {
    const state = get();
    if (state.waveInProgress || state.currentWave >= TOTAL_WAVES) return;
    
    const waveNum = state.currentWave + 1;
    const waveConfig = getWaveConfig(waveNum);
    const diffConfig = DIFFICULTIES[state.difficulty] || DIFFICULTIES.polished;
    
    // Convert gems to player units
    const playerUnits = [];
    for (let row = 0; row < GRID_ROWS; row++) {
      for (let col = 0; col < GRID_COLS; col++) {
        const gem = state.grid[row][col];
        if (gem) {
          playerUnits.push({
            id: gem.id,
            type: gem.type,
            tier: gem.tier,
            hp: 30 + (gem.tier * 15), // HP based on tier
            maxHp: 30 + (gem.tier * 15),
            damage: 10 + (gem.tier * 5), // Damage based on tier
            row,
            col
          });
        }
      }
    }
    
    // Create enemies with scaled HP, applying difficulty multiplier
    const hpMultiplier = waveNum === 1 ? diffConfig.wave1EnemyHpMultiplier : 1.0;
    const enemies = waveConfig.enemies.map((e, i) => ({
      id: `enemy-${waveNum}-${i}`,
      type: e.type,
      hp: Math.floor(e.hp * hpMultiplier),
      maxHp: Math.floor(e.hp * hpMultiplier),
      damage: e.damage,
      speed: e.speed
    }));
    
    set({
      currentWave: waveNum,
      waveInProgress: true,
      enemies,
      playerUnits
    });
  },
  
  // Process battle tick
  processBattle: () => {
    const state = get();
    if (!state.waveInProgress) return;
    
    const diffConfig = DIFFICULTIES[state.difficulty] || DIFFICULTIES.polished;
    let { enemies, playerUnits, cursedShards } = state;
    
    // Track which enemies died this tick for cursed shard spawning
    const enemiesBefore = enemies.length;
    
    // Player units attack enemies
    for (const unit of playerUnits) {
      if (unit.hp <= 0) continue;
      if (enemies.length === 0) break;
      
      const targetIndex = randomInt(0, enemies.length, state.rng);
      const target = enemies[targetIndex];
      
      if (target.hp > 0) {
        target.hp -= unit.damage;
        if (target.hp <= 0) {
          enemies = enemies.filter(e => e.id !== target.id);
        }
      }
    }
    
    // Enemies attack player units
    for (const enemy of enemies) {
      if (enemy.hp <= 0) continue;
      if (playerUnits.length === 0) break;
      
      const aliveUnits = playerUnits.filter(u => u.hp > 0);
      if (aliveUnits.length === 0) break;
      
      const targetIndex = randomInt(0, aliveUnits.length, state.rng);
      const target = aliveUnits[targetIndex];
      
      target.hp -= enemy.damage;
      if (target.hp < 0) target.hp = 0;
    }
    
    // Cursed difficulty: spawn shard on enemy death
    if (diffConfig.cursedShards) {
      const enemiesDied = enemiesBefore - enemies.length;
      for (let i = 0; i < enemiesDied; i++) {
        // Find empty cells (no gem, no existing shard)
        const emptyCells = [];
        for (let row = 0; row < GRID_ROWS; row++) {
          for (let col = 0; col < GRID_COLS; col++) {
            const hasGem = state.grid[row][col] !== null;
            const hasShard = cursedShards.some(s => s.row === row && s.col === col);
            if (!hasGem && !hasShard) {
              emptyCells.push({ row, col });
            }
          }
        }
        
        // Only spawn if there's an empty cell
        if (emptyCells.length > 0) {
          const pos = emptyCells[randomInt(0, emptyCells.length, state.rng)];
          cursedShards = [...cursedShards, { row: pos.row, col: pos.col }];
        }
        // If no empty cell, silently skip shard spawn
      }
    }
    
    // Check win/lose conditions
    const aliveUnits = playerUnits.filter(u => u.hp > 0);
    
    if (enemies.length === 0) {
      // Wave complete
      if (state.currentWave >= TOTAL_WAVES) {
        // Victory!
        set({ phase: 'victory', waveInProgress: false });
        
        // If beaten on Flawless difficulty, unlock Cursed
        if (state.difficulty === 'flawless') {
          try {
            localStorage.setItem(STORAGE_KEY_EXPERT_CLEARED, 'true');
          } catch (e) {
            // localStorage unavailable
          }
        }
      } else {
        set({
          waveInProgress: false,
          enemies: [],
          playerUnits: [],
          cursedShards
        });
      }
    } else if (aliveUnits.length === 0) {
      // All units dead
      set({
        waveInProgress: false,
        enemies: [],
        playerUnits: [],
        cursedShards
      });
      
      // Check if player can recover (has mana to spawn)
      if (state.mana < GEM_COSTS[1]) {
        set({ phase: 'gameover' });
      }
    } else {
      set({ enemies, playerUnits, cursedShards });
    }
  },
  
  // Toggle speed
  toggleSpeed: () => {
    const state = get();
    const newMultiplier = state.speedMultiplier === 1 ? 2 : 1;
    set({ speedMultiplier: newMultiplier });
  },
  
  // Update elapsed time
  updateElapsedTime: () => {
    const state = get();
    if (state.phase !== 'playing') return;
    
    const elapsed = (Date.now() - state.startTime) / 1000;
    set({ elapsedTime: elapsed });
  },
  
  // Check game over condition
  checkGameOver: () => {
    const state = get();
    if (state.phase !== 'playing') return;
    
    // Check if any gems exist
    const hasGems = state.grid.some(row => row.some(gem => gem !== null));
    
    // Check if player can afford a gem
    const canAffordGem = state.mana >= GEM_COSTS[1];
    
    if (!hasGems && !canAffordGem) {
      set({ phase: 'gameover' });
    }
  }
}));
